// Pure cost model for the grid-shooting calculator. All inputs are already
// resolved to plain numbers by the caller (tier multipliers and per-market
// currency applied upstream); this module only runs the time x rate formula.
//
// Core principle: film money is paid per booked block, not per actual minute of
// the task. A short grid shoot still pays the crew's minimum call and the stage's
// minimum booking; gear is a flat day rate. The two scenarios differ in whether
// that booking has already been paid for:
//   - addon:      gear + crew are already on the clock for the main shoot, so the
//                 marginal cost of grids is the crew's extra overtime hours plus
//                 post. Gear is free (inside its 24h rental window).
//   - standalone: a fresh booking made just for grids, so gear is billed at day
//                 rates and crew/stage at their minimum call/booking.

// A full day rate covers a standard 12-hour shift; dividing by it turns any per-day
// rate (the on-set supervisor) into the hourly used by the overtime/min-call math.
const SHIFT_HOURS = 12;

export function computeEstimate({ scenario, lensCount, rates, knobs }) {
  const n = Math.max(0, lensCount);
  // On-set time is a fixed one-time setup + teardown (rig at the start, strike at the
  // end — independent of lens count), plus per-lens time (the shoot itself and the
  // re-positioning/re-aligning for the next lens). Setup/teardown is not billed as its
  // own line: it just lengthens the on-set hours, so it is paid as overtime in add-on
  // and is absorbed by the crew minimum call in standalone (a half day already covers it).
  const onsetHours = Number(knobs.onset_base_hours || 0) + (knobs.onset_minutes_per_lens / 60) * n;
  const postHours = knobs.post_hours_per_lens * n;
  const ot = knobs.overtime_multiplier;
  const isAddon = scenario === 'addon';

  // One formula, driven entirely by the rates passed in: a line the caller does not
  // pay for (free / already owned / a role not on the shoot) arrives as 0 and drops
  // out. The scenario only changes the *hours*: a fresh standalone booking pays the
  // stage and crew for their minimum block, while add-on rides the main day, so its
  // minimum is already met and only the actual hours are added — at overtime.
  const crewHours = isAddon ? onsetHours : Math.max(onsetHours, knobs.crew_min_call_hours);
  const laborMult = isAddon ? ot : 1;

  // All on-set labor rides the same rails: the hourly crew (mechanic, electrician, DIT)
  // directly, plus the on-set supervisor whose per-day rate is converted to an hourly by
  // dividing by a 12h shift. So the supervisor is billed exactly like the crew — overtime
  // hours in add-on, the minimum call in standalone — not a flat day. Any rate at 0 (an
  // unticked line / a role not on the shoot) simply drops out.
  const laborHourly = rates.mechanic_1ac + rates.electrician + rates.dit_playback
    + rates.onset_supervisor / SHIFT_HOURS;

  // Rental: flat day rates for whatever gear is being paid for. The stage is a day
  // rate too (not billed hourly): a shoot books the space for the day like the camera,
  // so there is no separate minimum-booking knob — untick the stage if it is free.
  const rental = rates.studio_stage + rates.camera_package + rates.lens_each * n;

  // Labor: the whole on-set crew (including the supervisor) for the booked hours —
  // overtime in add-on, the minimum call in standalone. Material offload is assumed to
  // fit inside those already-billed hours, so it carries no separate line.
  const labor = laborHourly * crewHours * laborMult;

  // Post (studio artist, billed by the hour of work, never overtime).
  const post = rates.matchmove_artist * postHours;

  // Optional overhead (insurance, transport, agency, contingency): a percent of
  // the DIY subtotal. Defaults to 0, so it is invisible unless a market or the
  // user sets it. Money only; it never changes the hours figure.
  const subtotal = rental + labor + post;
  const overheadPct = knobs.overhead_pct ? Number(knobs.overhead_pct) : 0;
  const overhead = subtotal * (overheadPct / 100);

  const total = subtotal + overhead;
  // Hours are the visitor's actual effort (setup + shoot + post), not the billed
  // blocks: a crew minimum call does not add to the time you personally spend.
  const totalHours = onsetHours + postHours;
  return { onsetHours, postHours, totalHours, rental, labor, post, overhead, total };
}

// Production tier scales on-set rates (rental + crew), but NOT post: a matchmove
// artist delivers the same result whatever the shoot's budget, so the post group
// is tier-invariant. Returns a { key: number } rate map.
export function applyTier(items, tierMult) {
  const rates = {};
  items.forEach((it) => {
    rates[it.key] = Number(it.amount) * (it.group === 'post' ? 1 : tierMult);
  });
  return rates;
}

export function presetsCount(total, referencePrice) {
  const price = Number(referencePrice);
  if (!price || price <= 0) return 0;
  return Math.floor(total / price);
}
